import type { Router } from 'vue-router';

// Messages thrown by browsers when a dynamically imported chunk is missing on the server,
// typically because a new version was deployed while the page was open.
const CHUNK_LOAD_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

const RELOAD_KEY = 'chunk-reload-at';
const RELOAD_GUARD_MS = 10_000;

let reloading = false;

export function isChunkLoadError(err: unknown): boolean {
  return err instanceof Error && CHUNK_LOAD_ERROR.test(err.message);
}

/** Whether a reload triggered by a missing chunk is in progress */
export function isReloadingForUpdate(): boolean {
  return reloading;
}

/**
 * Reloads the page (optionally navigating to `url`) to fetch the latest version of the app.
 * Returns false without reloading if a reload was already attempted shortly before, to avoid reload loops.
 */
export function reloadForUpdate(url?: string): boolean {
  if (reloading) {
    return true;
  }

  const now = Date.now();
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY));
    if (last && now - last < RELOAD_GUARD_MS) {
      return false;
    }
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    // storage unavailable: reload anyway
  }

  reloading = true;
  if (url) {
    window.location.assign(url);
  } else {
    window.location.reload();
  }
  return true;
}

export function installChunkReload(router: Router) {
  window.addEventListener('vite:preloadError', event => {
    if (reloadForUpdate()) {
      event.preventDefault();
    }
  });

  router.onError((err, to) => {
    if (isChunkLoadError(err)) {
      reloadForUpdate(router.resolve(to).href);
    }
  });
}
